import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { leadAPI } from '@/lib/api';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { jobCategoryOptions } from '@/lib/jobCategory';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Phone, Mail, MapPin, Calendar, Clock, User, 
  ArrowLeft, MessageSquare, Send, AlertCircle,
  ExternalLink, Tag, History, Loader2, UserCheck, ArrowRightCircle, Pencil, Save, X, Wrench, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { EmailComposeDialog } from '@/components/EmailComposeDialog';
import { PhotoGallery } from '@/components/PhotoGallery';
import { AiEstimationLeadSection } from '@/components/AiEstimationLeadSection';

export function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [lead, setLead] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [converting, setConverting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [convertForm, setConvertForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', address: '', description: '',
  });
  // SMS conversation thread (V1 bidirectional)
  const [smsMessages, setSmsMessages] = useState([]);
  const [smsBody, setSmsBody] = useState('');
  const [sendingSms, setSendingSms] = useState(false);

  // Delete lead — confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDeleteLead = Boolean(user?.permissions?.can_edit_leads);

  const STATUS_OPTIONS = [
    { value: 'new', label: t('status.new'), color: 'bg-blue-500' },
    { value: 'contacted', label: t('status.contacted'), color: 'bg-yellow-500' },
    { value: 'appointment', label: t('status.appointment'), color: 'bg-purple-500' },
    { value: 'quote_sent', label: t('status.quote_sent'), color: 'bg-orange-500' },
    { value: 'won', label: t('status.won'), color: 'bg-green-500' },
    { value: 'lost', label: t('status.lost'), color: 'bg-red-500' },
  ];

  useEffect(() => {
    loadLead();
  }, [leadId]);

  const loadLead = async () => {
    try {
      const response = await leadAPI.get(leadId);
      setLead(response.data);
    } catch (err) {
      console.error('Error loading lead:', err);
      toast.error(t('leads.noLeadsFound'));
      navigate('/leads');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = () => {
    const nameParts = (lead.name || '').trim().split(' ', 2);
    setEditForm({
      first_name: lead.first_name || nameParts[0] || '',
      last_name: lead.last_name || nameParts.slice(1).join(' ') || '',
      email: lead.email || '',
      phone: lead.phone || '',
      address: lead.address || '',
      city: lead.city || '',
      work_type: lead.work_type || lead.service || '',
      desired_date: lead.desired_date || '',
    });
    setIsEditing(true);
  };

  const isValidEmail = (e) => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const formatPhone = (v) => {
    const d = v.replace(/[^\d]/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
    return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
  };
  const isValidPhone = (p) => !p || /^\d{3}-\d{3}-\d{4}$/.test(p);

  const handleEditSave = async () => {
    if (editForm.email && !isValidEmail(editForm.email)) {
      toast.error(language === 'fr' ? 'Format de courriel invalide' : 'Invalid email format');
      return;
    }
    if (editForm.phone && !isValidPhone(editForm.phone)) {
      toast.error(language === 'fr' ? 'Numero de telephone invalide (ex: 514-555-1234)' : 'Invalid phone number');
      return;
    }
    setEditSaving(true);
    try {
      const payload = {
        ...editForm,
        name: `${editForm.first_name} ${editForm.last_name}`.trim(),
      };
      const response = await leadAPI.update(leadId, payload);
      setLead(response.data);
      setIsEditing(false);
      toast.success(language === 'fr' ? 'Prospect mis a jour' : 'Lead updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setEditSaving(false);
    }
  };

  const loadEmailHistory = async () => {
    setLoadingEmails(true);
    try {
      const { data } = await api.get(`/email/history/${leadId}`);
      setEmailHistory(data.emails);
    } catch (e) { console.error('loadEmailHistory failed:', e); }
    finally { setLoadingEmails(false); }
  };

  useEffect(() => { if (leadId) loadEmailHistory(); }, [leadId]);

  // SMS conversation: load + lightweight polling every 12s. Stops on
  // unmount. No websocket, no complex state.
  const loadSms = async () => {
    if (!leadId) return;
    try {
      const { data } = await api.get(`/leads/${leadId}/sms`);
      setSmsMessages(data.messages || []);
    } catch (e) { console.error('loadSms failed:', e); }
  };
  useEffect(() => {
    if (!leadId) return undefined;
    loadSms();
    const id = setInterval(loadSms, 12000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const sendSms = async () => {
    const body = smsBody.trim();
    if (!body || !leadId) return;
    setSendingSms(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/sms`, { body });
      if (data && data.error) {
        toast.error(data.message || (language === 'fr' ? 'Envoi échoué' : 'Send failed'));
      } else {
        setSmsBody('');
        toast.success(language === 'fr' ? 'SMS envoyé' : 'SMS sent');
        await loadSms();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Erreur envoi SMS' : 'SMS send error'));
    } finally {
      setSendingSms(false);
    }
  };

  const [reactivatingSms, setReactivatingSms] = useState(false);
  const reactivateSms = async () => {
    if (!leadId) return;
    const confirmMsg = language === 'fr'
      ? 'Réactiver les SMS pour ce prospect ? Vérifiez qu\'il a explicitement consenti (réponse START / OUI).'
      : 'Re-enable SMS for this lead? Make sure they explicitly consented (replied START / YES).';
    if (!window.confirm(confirmMsg)) return;
    setReactivatingSms(true);
    try {
      await api.post(`/leads/${leadId}/sms/reactivate`);
      toast.success(language === 'fr' ? 'SMS réactivés' : 'SMS re-enabled');
      await loadLead();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Réactivation échouée' : 'Reactivation failed'));
    } finally {
      setReactivatingSms(false);
    }
  };

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const { data } = await api.get('/employees');
        setEmployees((data.employees || []).filter(e => e.status === 'actif'));
      } catch (e) { console.error('loadEmployees failed:', e); }
    };
    loadEmployees();
  }, []);

  // Sales reps (separate from field workers / employees). Loaded once.
  const [salesReps, setSalesReps] = useState([]);
  useEffect(() => {
    const loadSalesReps = async () => {
      try {
        const { data } = await api.get('/leads/sales-reps/list');
        setSalesReps(data.sales_reps || []);
      } catch (e) { console.error('loadSalesReps failed:', e); }
    };
    loadSalesReps();
  }, []);

  const handleAssignSalesRep = async (repId) => {
    try {
      const response = await leadAPI.update(leadId, { assigned_sales_rep_id: repId });
      setLead(response.data);
      if (repId) {
        const rep = salesReps.find(r => r.user_id === repId);
        const repName = rep?.name || rep?.email || repId;
        toast.success(language === 'fr' ? `Représentant assigné à ${repName}` : `Representative assigned to ${repName}`);
      } else {
        toast.success(language === 'fr' ? 'Représentant retiré' : 'Representative removed');
      }
    } catch (err) {
      console.error('Error assigning sales rep:', err);
      toast.error(t('common.error'));
    }
  };

  const handleAssignRepresentative = async (employeeId) => {
    try {
      const response = await leadAPI.update(leadId, { assigned_representative_id: employeeId });
      setLead(response.data);
      toast.success(language === 'fr' ? 'Employé assigné' : 'Employee assigned');
    } catch (err) {
      console.error('Error assigning representative:', err);
      toast.error(t('common.error'));
    }
  };

  const openConvertDialog = () => {
    const nameParts = (lead.name || '').trim().split(' ', 2);
    setConvertForm({
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      email: lead.email || '',
      phone: lead.phone || '',
      address: lead.address || '',
      description: lead.message || '',
    });
    setShowConvertDialog(true);
  };

  const handleConvert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/clients/convert/${leadId}`, {
        ...convertForm,
        assigned_representative_id: lead.assigned_representative_id || '',
      });
      toast.success(language === 'fr' ? 'Prospect converti en client!' : 'Prospect converted to client!');
      setShowConvertDialog(false);
      navigate(`/clients/${data.client.client_id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || (language === 'fr' ? 'Erreur de conversion' : 'Conversion error');
      toast.error(msg);
    } finally {
      setConverting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await leadAPI.update(leadId, { status: newStatus });
      setLead(response.data);
      toast.success(t('leads.statusUpdated'));
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error(t('common.error'));
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setIsSavingNote(true);
    try {
      const response = await leadAPI.addNote(leadId, newNote);
      setLead(response.data);
      setNewNote('');
      toast.success(t('leads.noteAdded'));
    } catch (err) {
      console.error('Error adding note:', err);
      toast.error(t('common.error'));
    } finally {
      setIsSavingNote(false);
    }
  };

  /**
   * Permanently delete the current lead (hard delete + cascade cleanup
   * server-side). Closes the dialog and redirects to /leads on success.
   * Generic error toast on failure — the backend already enforces the
   * permission and workspace isolation, so a 403/404 is a real refusal
   * we should surface accurately.
   */
  const handleDeleteLead = async () => {
    setIsDeleting(true);
    try {
      await leadAPI.delete(leadId);
      toast.success(language === 'fr' ? 'Prospect supprimé' : 'Lead deleted');
      navigate('/leads', { replace: true });
    } catch (err) {
      const code = err.response?.status;
      if (code === 403) {
        toast.error(language === 'fr' ? 'Vous n\'avez pas la permission.' : 'You do not have permission.');
      } else if (code === 404) {
        toast.error(language === 'fr' ? 'Prospect introuvable.' : 'Lead not found.');
      } else {
        toast.error(err.response?.data?.detail || t('common.error'));
      }
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSourceLabel = (source) => t(`source.${source}`);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!lead) {
    // The lead row was deleted or the user lost access. `loadLead`'s
    // catch block already toasted + queued navigate('/leads'); render the
    // same spinner fallback during that single-frame transition instead
    // of `return null` (which paints a black/blank content area).
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentStatus = STATUS_OPTIONS.find(s => s.value === lead.status);

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/leads">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight flex items-center gap-3">
              {lead.name}
              {lead.is_urgent && (
                <Badge variant="destructive" className="ml-2">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {t('leads.urgent')}
                </Badge>
              )}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('leads.createdOn', { date: formatDate(lead.created_at) })}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {lead.email && (
            <Button variant="outline" onClick={() => setShowEmailCompose(true)} data-testid="send-email-btn">
              <Mail className="mr-2 h-4 w-4" />
              {language === 'fr' ? 'Envoyer email' : 'Send email'}
            </Button>
          )}
          {lead.status !== 'won' && !lead.converted_at && (
            <Button onClick={openConvertDialog} className="bg-green-600 hover:bg-green-700 text-white" data-testid="convert-to-client-btn">
              <ArrowRightCircle className="mr-2 h-4 w-4" />
              {language === 'fr' ? 'Convertir en client' : 'Convert to client'}
            </Button>
          )}
          {lead.converted_at && (
            <Badge className="bg-green-100 text-green-700 border-green-300" data-testid="converted-badge">
              <UserCheck className="mr-1 h-3.5 w-3.5" />
              {language === 'fr' ? 'Converti' : 'Converted'}
            </Badge>
          )}
          <Select value={lead.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[200px]" data-testid="status-select">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${currentStatus?.color}`} />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${opt.color}`} />
                    {opt.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canDeleteLead && (
            <Button
              variant="outline"
              className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setShowDeleteDialog(true)}
              data-testid="delete-lead-btn"
              title={language === 'fr' ? 'Supprimer le prospect' : 'Delete prospect'}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {t('leads.contactInfo')}
                </CardTitle>
                {!isEditing ? (
                  <Button variant="outline" size="sm" onClick={startEditing} data-testid="edit-lead-btn">
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    {language === 'fr' ? 'Modifier' : 'Edit'}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} data-testid="cancel-edit-btn">
                      <X className="h-3.5 w-3.5 mr-1" />
                      {language === 'fr' ? 'Annuler' : 'Cancel'}
                    </Button>
                    <Button size="sm" onClick={handleEditSave} disabled={editSaving} data-testid="save-lead-btn">
                      {editSaving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                      {language === 'fr' ? 'Sauvegarder' : 'Save'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>{language === 'fr' ? 'Prenom' : 'First name'}</Label>
                      <Input value={editForm.first_name} onChange={e => setEditForm(f => ({...f, first_name: e.target.value}))} data-testid="edit-lead-firstname" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{language === 'fr' ? 'Nom' : 'Last name'}</Label>
                      <Input value={editForm.last_name} onChange={e => setEditForm(f => ({...f, last_name: e.target.value}))} data-testid="edit-lead-lastname" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {t('leads.phone')}</Label>
                      <Input value={editForm.phone} onChange={e => setEditForm(f => ({...f, phone: formatPhone(e.target.value)}))} data-testid="edit-lead-phone" className={editForm.phone && !isValidPhone(editForm.phone) ? 'border-red-500' : ''} />
                      {editForm.phone && !isValidPhone(editForm.phone) && <p className="text-xs text-red-500 mt-1">{language === 'fr' ? 'Format: 514-555-1234' : 'Format: 514-555-1234'}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {language === 'fr' ? 'Courriel' : 'Email'}</Label>
                      <Input type="email" value={editForm.email} onChange={e => setEditForm(f => ({...f, email: e.target.value}))} data-testid="edit-lead-email" className={editForm.email && !isValidEmail(editForm.email) ? 'border-red-500' : ''} />
                      {editForm.email && !isValidEmail(editForm.email) && <p className="text-xs text-red-500 mt-1">{language === 'fr' ? 'Format invalide (ex: nom@domaine.com)' : 'Invalid format'}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {language === 'fr' ? 'Adresse' : 'Address'}</Label>
                      <Input value={editForm.address} onChange={e => setEditForm(f => ({...f, address: e.target.value}))} data-testid="edit-lead-address" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {language === 'fr' ? 'Ville' : 'City'}</Label>
                      <Input value={editForm.city} onChange={e => setEditForm(f => ({...f, city: e.target.value}))} data-testid="edit-lead-city" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> {language === 'fr' ? 'Type de travaux' : 'Work type'}</Label>
                      <Input value={editForm.work_type} onChange={e => setEditForm(f => ({...f, work_type: e.target.value}))} placeholder={language === 'fr' ? 'Ex: Terrassement, Excavation' : 'E.g. Landscaping'} data-testid="edit-lead-worktype" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {language === 'fr' ? 'Date souhaitee' : 'Desired date'}</Label>
                      <Input type="date" value={editForm.desired_date} onChange={e => setEditForm(f => ({...f, desired_date: e.target.value}))} data-testid="edit-lead-date" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(lead.first_name || lead.last_name) && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <User className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Nom complet' : 'Full name'}</p>
                        <p className="font-semibold">{lead.first_name} {lead.last_name}</p>
                      </div>
                    </div>
                  )}
                  {lead.phone && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Phone className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">{t('leads.phone')}</p>
                        <a href={`tel:${lead.phone}`} className="font-semibold hover:text-primary">{lead.phone}</a>
                      </div>
                    </div>
                  )}
                  {lead.email && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Mail className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Courriel' : 'Email'}</p>
                        <a href={`mailto:${lead.email}`} className="font-semibold hover:text-primary truncate block">{lead.email}</a>
                      </div>
                    </div>
                  )}
                  {lead.address && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <MapPin className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Adresse' : 'Address'}</p>
                        <p className="font-semibold">{lead.address}</p>
                      </div>
                    </div>
                  )}
                  {lead.city && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <MapPin className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Ville' : 'City'}</p>
                        <p className="font-semibold">{lead.city}</p>
                      </div>
                    </div>
                  )}
                  {lead.postal_code && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg" data-testid="lead-postal-code">
                      <MapPin className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Code postal' : 'Postal code'}</p>
                        <p className="font-semibold">{lead.postal_code}</p>
                      </div>
                    </div>
                  )}
                  {lead.work_type && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Wrench className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Type de travaux' : 'Work type'}</p>
                        <p className="font-semibold">{lead.work_type}</p>
                      </div>
                    </div>
                  )}
                  {lead.desired_date && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Calendar className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Date souhaitee' : 'Desired date'}</p>
                        <p className="font-semibold">{new Date(lead.desired_date).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sales representative assignment — separate from field workers.
              Saves immediately on change and emits an audit history event. */}
          <Card data-testid="lead-sales-rep-section">
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-primary" />
                {language === 'fr' ? 'Représentant assigné' : 'Assigned representative'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Select
                value={lead.assigned_sales_rep_id || 'none'}
                onValueChange={(v) => handleAssignSalesRep(v === 'none' ? '' : v)}
              >
                <SelectTrigger className="w-full sm:w-96" data-testid="lead-sales-rep-select">
                  <SelectValue placeholder={language === 'fr' ? 'Aucun' : 'None'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="lead-sales-rep-none">
                    {language === 'fr' ? 'Aucun' : 'None'}
                  </SelectItem>
                  {salesReps.map((r) => (
                    <SelectItem key={r.user_id} value={r.user_id} data-testid={`lead-sales-rep-${r.user_id}`}>
                      {r.name || r.email}
                      {r.role !== 'sales_representative' && (
                        <span className="text-xs text-muted-foreground ml-2">
                          ({r.role === 'admin_enterprise'
                            ? (language === 'fr' ? 'Admin Entreprise' : 'Enterprise Admin')
                            : (language === 'fr' ? 'Admin' : 'Admin')})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lead.assigned_sales_rep_id && (() => {
                const rep = salesReps.find((r) => r.user_id === lead.assigned_sales_rep_id);
                if (!rep) return null;
                return (
                  <p className="text-sm text-muted-foreground" data-testid="lead-sales-rep-current">
                    {language === 'fr' ? 'Suivi par' : 'Followed by'}: <span className="font-medium text-foreground">{rep.name || rep.email}</span>
                  </p>
                );
              })()}
            </CardContent>
          </Card>

          {/* AI Estimation answers — only for leads captured via an ai_estimation form. */}
          {lead.form_type === 'ai_estimation' && (
            <AiEstimationLeadSection lead={lead} language={language} />
          )}

          {/* Service Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-bold">{t('leads.requestDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Job category — editable inline. Persists immediately. */}
              <div data-testid="lead-job-category-section">
                <p className="text-sm text-muted-foreground mb-1">
                  {language === 'fr' ? 'Type de job' : 'Job type'}
                </p>
                <Select
                  value={lead.job_category || 'unset'}
                  onValueChange={async (v) => {
                    const next = v === 'unset' ? null : v;
                    try {
                      const { data } = await leadAPI.update(leadId, { job_category: next });
                      setLead(data);
                      toast.success(language === 'fr' ? 'Type de job mis a jour' : 'Job type updated');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
                    }
                  }}
                >
                  <SelectTrigger className="w-full sm:w-80" data-testid="lead-job-category-select">
                    <SelectValue placeholder={language === 'fr' ? 'Choisir...' : 'Choose...'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">{language === 'fr' ? 'Non spécifié' : 'Not set'}</SelectItem>
                    {jobCategoryOptions(language).map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`lead-job-category-${o.value}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {lead.service && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('leads.requestedService')}</p>
                    <Badge variant="secondary" className="text-sm">
                      {lead.service}
                    </Badge>
                  </div>
                )}
                
                {lead.problem_type && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('leads.problemType')}</p>
                    <p className="font-medium">{lead.problem_type}</p>
                  </div>
                )}
                
                {lead.budget_range && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('leads.estimatedBudget')}</p>
                    <p className="font-medium">{lead.budget_range}</p>
                  </div>
                )}
              </div>
              
              {lead.message && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">{t('leads.message')}</p>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="whitespace-pre-wrap">{lead.message}</p>
                  </div>
                </div>
              )}

              {/* Custom Fields */}
              {lead.custom_fields && Object.keys(lead.custom_fields).length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">{t('leads.otherInfo')}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(lead.custom_fields)
                      .filter(([key]) => !['name', 'email', 'phone', 'city', 'service', 'message', '_honeypot'].includes(key))
                      .map(([key, value]) => (
                        <div key={key} className="p-2 bg-muted/30 rounded">
                          <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="text-sm font-medium">{String(value)}</p>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                {t('leads.notes')} ({lead.notes?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Note */}
              <div className="flex gap-2">
                <Textarea
                  placeholder={t('leads.addNotePlaceholder')}
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="min-h-[80px]"
                  data-testid="note-input"
                />
              </div>
              <Button 
                onClick={handleAddNote} 
                disabled={!newNote.trim() || isSavingNote}
                className="w-full sm:w-auto"
                data-testid="add-note-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                {t('leads.addNote')}
              </Button>

              <Separator />

              {/* Notes List */}
              {lead.notes?.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  {t('leads.noNotes')}
                </p>
              ) : (
                <div className="space-y-4">
                  {lead.notes?.slice().reverse().map((note) => (
                    <div key={note.note_id} className="p-4 bg-muted/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-sm">{note.author_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(note.created_at)}
                        </p>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Source & Tracking */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <ExternalLink className="h-5 w-5" />
                {t('leads.source')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('leads.source')}</p>
                <Badge variant="outline" className={`source-${lead.source}`}>
                  {getSourceLabel(lead.source)}
                </Badge>
              </div>
              
              {lead.tracking && (
                <>
                  {lead.tracking.utm_source && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">UTM Source</p>
                      <p className="text-sm font-mono">{lead.tracking.utm_source}</p>
                    </div>
                  )}
                  {lead.tracking.utm_medium && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">UTM Medium</p>
                      <p className="text-sm font-mono">{lead.tracking.utm_medium}</p>
                    </div>
                  )}
                  {lead.tracking.utm_campaign && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">UTM Campaign</p>
                      <p className="text-sm font-mono">{lead.tracking.utm_campaign}</p>
                    </div>
                  )}
                  {lead.tracking.referrer && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Referrer</p>
                      <p className="text-sm font-mono truncate">{lead.tracking.referrer}</p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Assigned Representative */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                {language === 'fr' ? 'Employés assignés' : 'Assigned employees'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={lead.assigned_representative_id || '_none'}
                onValueChange={(val) => handleAssignRepresentative(val === '_none' ? '' : val)}
              >
                <SelectTrigger data-testid="assigned-rep-select">
                  <SelectValue placeholder={language === 'fr' ? 'Selectionner...' : 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{language === 'fr' ? 'Aucun' : 'None'}</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.first_name} {emp.last_name}{emp.position ? ` (${emp.position})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lead.assigned_representative_id && employees.length > 0 && (() => {
                const rep = employees.find(e => e.employee_id === lead.assigned_representative_id);
                return rep ? (
                  <div className="mt-3 p-3 bg-muted/40 rounded-lg text-sm">
                    <p className="font-medium">{rep.first_name} {rep.last_name}</p>
                    {rep.phone && <p className="text-muted-foreground flex items-center gap-1 mt-1"><Phone className="h-3 w-3" /> {rep.phone}</p>}
                    {rep.email && <p className="text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> {rep.email}</p>}
                  </div>
                ) : null;
              })()}
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Tag className="h-5 w-5" />
                {t('leads.tags')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lead.tags?.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('leads.noTags')}</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {lead.tags?.map((tag, i) => (
                    <Badge key={i} variant="secondary">{tag}</Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <History className="h-5 w-5" />
                {t('leads.history')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                  <div>
                    <p className="text-sm font-medium">{t('leads.leadCreated')}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(lead.created_at)}
                    </p>
                  </div>
                </div>
                
                {lead.contacted_at && (
                  <div className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2" />
                    <div>
                      <p className="text-sm font-medium">{t('leads.firstContact')}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(lead.contacted_at)}
                      </p>
                    </div>
                  </div>
                )}
                
                {lead.converted_at && (
                  <div className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                    <div>
                      <p className="text-sm font-medium">{t('leads.converted')}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(lead.converted_at)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* SMS Conversation Section — V1 bidirectional */}
      <Card data-testid="lead-sms-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold flex items-center justify-between gap-2 flex-wrap">
            <span className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              {language === 'fr' ? 'Conversation SMS' : 'SMS conversation'}
              {smsMessages.length > 0 && (
                <Badge variant="outline" className="text-xs">{smsMessages.length}</Badge>
              )}
            </span>
            {lead.phone && (
              <span className="text-xs font-normal text-muted-foreground">{lead.phone}</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Thread */}
          <div
            className="space-y-2 max-h-[360px] overflow-y-auto rounded-lg bg-muted/30 p-3"
            data-testid="lead-sms-thread"
          >
            {smsMessages.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                {language === 'fr'
                  ? "Aucun SMS pour ce prospect. Envoyez le premier ci-dessous."
                  : 'No SMS yet for this lead. Send the first one below.'}
              </p>
            )}
            {smsMessages.map(m => {
              const outbound = m.direction === 'outbound';
              const failed = m.status === 'failed';
              return (
                <div
                  key={m.message_id}
                  className={outbound ? 'flex justify-end' : 'flex justify-start'}
                >
                  <div
                    className={
                      'max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ' +
                      (outbound
                        ? (failed
                            ? 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30 rounded-br-sm'
                            : 'bg-primary text-primary-foreground rounded-br-sm')
                        : 'bg-card border rounded-bl-sm')
                    }
                    data-testid={`lead-sms-bubble-${m.direction}`}
                  >
                    <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    <p className={'mt-1 text-[10px] ' + (outbound && !failed ? 'opacity-70' : 'text-muted-foreground')}>
                      {new Date(m.created_at).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA', {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                      {failed && (
                        <span className="ml-2 font-semibold">
                          · {language === 'fr' ? 'échec' : 'failed'}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Composer */}
          {!lead.phone ? (
            <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {language === 'fr'
                ? "Ce prospect n'a pas de numéro de téléphone. Ajoutez-en un pour répondre par SMS."
                : 'This lead has no phone number. Add one to reply by SMS.'}
            </p>
          ) : lead.sms_opt_out ? (
            <div
              className="space-y-2 rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-600 dark:text-red-400"
              data-testid="lead-sms-opt-out-badge"
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {language === 'fr'
                    ? "SMS désactivé — ce prospect a répondu STOP / DESABONNER. Tout envoi (manuel ou automatique) est bloqué."
                    : 'SMS disabled — this lead replied STOP / UNSUBSCRIBE. All outbound SMS (manual or automatic) are blocked.'}
                </span>
              </div>
              {(user?.role === 'admin' || user?.role === 'admin_enterprise') && (
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={reactivateSms}
                    disabled={reactivatingSms}
                    data-testid="lead-sms-reactivate-btn"
                  >
                    {reactivatingSms ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      language === 'fr' ? 'Réactiver SMS' : 'Re-enable SMS'
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Textarea
                value={smsBody}
                onChange={(e) => setSmsBody(e.target.value.slice(0, 1600))}
                placeholder={language === 'fr'
                  ? 'Tapez votre réponse au client…'
                  : 'Type your reply to the client…'}
                rows={3}
                className="resize-none"
                disabled={sendingSms}
                data-testid="lead-sms-composer"
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">
                  {smsBody.length}/1600
                  {smsBody.length > 160 && (
                    <span className="ml-2">
                      · {Math.ceil(smsBody.length / 153)} {language === 'fr' ? 'segments' : 'segments'}
                    </span>
                  )}
                </span>
                <Button
                  size="sm"
                  onClick={sendSms}
                  disabled={sendingSms || !smsBody.trim()}
                  data-testid="lead-sms-send"
                >
                  {sendingSms ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      {language === 'fr' ? 'Envoyer SMS' : 'Send SMS'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email History Section */}
      {emailHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {language === 'fr' ? 'Historique emails' : 'Email history'} ({emailHistory.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {emailHistory.map(em => (
                <details key={em.email_id} className="group border rounded-lg">
                  <summary className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge className={em.status === 'sent' ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-500'}>
                        {em.status === 'sent' ? (language === 'fr' ? 'Envoye' : 'Sent') : (language === 'fr' ? 'Erreur' : 'Error')}
                      </Badge>
                      {em.email_type && (
                        <Badge variant="outline" className="text-[10px] shrink-0">
                          {em.email_type === 'confirm_prospect' ? (language === 'fr' ? 'Confirmation' : 'Confirmation') :
                           em.email_type === 'notify_admin' ? (language === 'fr' ? 'Notification' : 'Notification') :
                           em.email_type === 'followup_24h' ? (language === 'fr' ? 'Relance 24h' : '24h Follow-up') :
                           em.email_type === 'followup_72h' ? (language === 'fr' ? 'Relance 72h' : '72h Follow-up') :
                           em.email_type}
                        </Badge>
                      )}
                      <span className="text-sm font-medium truncate">{em.subject}</span>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0 ml-2">
                      {new Date(em.sent_at).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                    </span>
                  </summary>
                  <div className="px-3 pb-3 pt-1 border-t">
                    <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'A' : 'To'}: {em.to_email}</div>
                    <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(em.body || '') }} />
                  </div>
                </details>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photos */}
      {lead && (
        <PhotoGallery entityType="lead" entityId={lead.lead_id} />
      )}

      {/* Email Compose Dialog */}
      <EmailComposeDialog
        open={showEmailCompose}
        onClose={() => { setShowEmailCompose(false); loadEmailHistory(); }}
        lead={lead}
      />

      {/* Convert to Client Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightCircle className="h-5 w-5 text-green-600" />
              {language === 'fr' ? 'Convertir en client' : 'Convert to client'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Verifiez les informations avant de convertir ce prospect en client.'
                : 'Review the information before converting this prospect to a client.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{language === 'fr' ? 'Prenom' : 'First name'}</Label>
                <Input
                  value={convertForm.first_name}
                  onChange={e => setConvertForm(f => ({ ...f, first_name: e.target.value }))}
                  data-testid="convert-firstname"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{language === 'fr' ? 'Nom' : 'Last name'}</Label>
                <Input
                  value={convertForm.last_name}
                  onChange={e => setConvertForm(f => ({ ...f, last_name: e.target.value }))}
                  data-testid="convert-lastname"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={convertForm.email}
                  onChange={e => setConvertForm(f => ({ ...f, email: e.target.value }))}
                  data-testid="convert-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{language === 'fr' ? 'Telephone' : 'Phone'}</Label>
                <Input
                  value={convertForm.phone}
                  onChange={e => setConvertForm(f => ({ ...f, phone: e.target.value }))}
                  data-testid="convert-phone"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{language === 'fr' ? 'Adresse' : 'Address'}</Label>
              <Input
                value={convertForm.address}
                onChange={e => setConvertForm(f => ({ ...f, address: e.target.value }))}
                data-testid="convert-address"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description / Notes</Label>
              <Textarea
                value={convertForm.description}
                onChange={e => setConvertForm(f => ({ ...f, description: e.target.value }))}
                rows={3}
                data-testid="convert-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              onClick={handleConvert}
              disabled={converting || (!convertForm.first_name && !convertForm.last_name)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-convert-btn"
            >
              {converting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {language === 'fr' ? 'Convertir' : 'Convert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => !isDeleting && setShowDeleteDialog(open)}>
        <DialogContent data-testid="delete-lead-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Supprimer ce prospect ?' : 'Delete this lead?'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? `Cette action est irréversible. Le prospect "${lead.name}" ainsi que ses notes, photos, SMS et historique d'emails seront définitivement supprimés.`
                : `This action cannot be undone. The lead "${lead.name}" and all its notes, photos, SMS, and email history will be permanently deleted.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
              data-testid="cancel-delete-lead-btn"
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteLead}
              disabled={isDeleting}
              data-testid="confirm-delete-lead-btn"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {language === 'fr' ? 'Supprimer définitivement' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
